import React from 'react';
import { Link } from 'react-router-dom';

import { getJSON, patchJSON, postJSON } from '../../api/client.js';
import {
  CrudPage, UniActionButton, UniPageShell, UniSection, UniStatusPill, UniTable, useUniList,
} from '../waba/WabaShared.jsx';
import {
  SB_API, THEME, HOME, CopyField, ForceNextButton, OnboardChecks, TechDetails,
  formatInTimeZone, useDisplayTimezone,
} from './SignalBridgeShared.jsx';
import { copyToClipboard } from '../uni/UniShared.jsx';
import {
  SignalBridgeEventsWizard, SignalBridgePixelWizard, SignalBridgeStoreWizard,
} from './SignalBridgeWizards.jsx';
import { SignalBridgeGoogleWizard } from './SignalBridgeMore.jsx';
import { TestEventForm } from './SignalBridgePages.jsx';
import { BuyerReportBody } from './BuyerInsights.jsx';

const P = { theme: THEME, api: SB_API, home: HOME };

export function SignalBridgeDestinations() {
  const pixels = useUniList(`${SB_API}/pixels/`);
  const opts = (pixels.rows || []).map((p) => ({ value: p.id, label: p.name }));
  const [test, setTest] = React.useState(null);
  const [testCode, setTestCode] = React.useState('');
  const sendTest = async (row) => {
    try {
      const data = await postJSON(`${SB_API}/destinations/${row.id}/test/`, {
        test_event_code: testCode,
      });
      setTest(data);
    } catch (err) {
      setTest({ error: String(err.message || err) });
    }
  };
  return (
    <>
      <CrudPage
        {...P} helpPage="destinations" icon="↗" title="Destinations"
        endpoint={`${SB_API}/destinations/`}
        searchPlaceholder="Search destinations…"
        createLabel="+ Destination"
        fields={[
          { key: 'name', label: 'Name', required: true },
          { key: 'destination_type', label: 'Type', type: 'select', required: true, options: [
            { value: 'META_CAPI', label: 'Meta CAPI' },
            { value: 'GOOGLE_ADS', label: 'Google Ads (prefer Connect Google Ads wizard)' },
            { value: 'GENERIC_WEBHOOK', label: 'Generic webhook' },
          ] },
          { key: 'pixel_account', label: 'Pixel (Meta CAPI)', type: 'select', options: opts },
          { key: 'send_refund_events', label: 'Send Refund as Meta custom event', type: 'check' },
          { key: 'target_url', label: 'Target URL (webhook)' },
          { key: 'http_method', label: 'HTTP method', type: 'select', options: [
            { value: 'POST', label: 'POST' },
            { value: 'PUT', label: 'PUT' },
          ] },
          { key: 'auth_mode', label: 'Webhook auth', type: 'select', options: [
            { value: 'NONE', label: 'None' },
            { value: 'BEARER_TOKEN', label: 'Bearer' },
            { value: 'BASIC', label: 'Basic' },
            { value: 'HMAC_SHA256_SIGNATURE', label: 'HMAC-SHA256' },
          ] },
          { key: 'signing_secret', label: 'Signing secret', type: 'password', hint: 'Leave blank on edit to keep. Last 4 shown in view.' },
          { key: 'bearer_token', label: 'Bearer token', type: 'password' },
          { key: 'basic_username', label: 'Basic username' },
          { key: 'basic_password', label: 'Basic password', type: 'password' },
          { key: 'timeout_seconds', label: 'Timeout seconds' },
          { key: 'payload_template', label: 'Payload template', type: 'textarea', hint: 'Empty = send normalized JSON. Use {{event_id}} / {{event.value}}.' },
          { key: 'is_active', label: 'Active', type: 'check' },
        ]}
        columns={[
          { key: 'name', label: 'Name', render: (r) => <strong>{r.name}</strong> },
          { key: 'destination_type', label: 'Type', render: (r) => <UniStatusPill value={r.destination_type} /> },
          { key: 'pixel_name', label: 'Pixel' },
          { key: 'value_rule_mode', label: 'Value rule' },
          { key: 'send_refund_events', label: 'Refunds', render: (r) => (r.send_refund_events ? 'Yes' : 'No') },
          { key: 'target_url', label: 'URL' },
          { key: 'error_streak', label: 'Error streak', align: 'right' },
          { key: 'health_status', label: 'Health', render: (r) => <UniStatusPill value={r.health_status || 'UNKNOWN'} /> },
          { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
        ]}
        extraRowActions={(r) => ([
          { key: 'test', label: 'Send test payload', onClick: () => sendTest(r) },
        ])}
      />
      <div style={{ margin: '0 24px 12px', maxWidth: 360 }}>
        <label className="sb-test-aside__label">
          Test event code (Meta CAPI — used for this send only, not saved on the pixel)
          <input
            value={testCode}
            onChange={(e) => setTestCode(e.target.value)}
            placeholder="TEST12345"
          />
        </label>
      </div>
      {test && (
        <div style={{ margin: '0 0 24px' }}>
          <p>{test.error ? 'Test payload did not send.' : 'Test payload sent.'}</p>
          <TechDetails title="Result" data={test} />
        </div>
      )}
    </>
  );
}

export function SignalBridgeRoutes() {
  return (
    <CrudPage
      {...P} helpPage="routes" icon="⤳" title="Event routes"
      endpoint={`${SB_API}/routes/`}
      searchPlaceholder="Search routes…"
      createLabel="+ Route"
      fields={[
        { key: 'name', label: 'Name', required: true },
        { key: 'event_types', label: 'Event types (JSON list, empty = all)', hint: '["Purchase"]' },
        { key: 'filters', label: 'Filters (JSON AND)', type: 'textarea', hint: '{"payment_type":"COD","value_gte":0}' },
          { key: 'destination_ids', label: 'Destination ids (JSON list)', type: 'json', hint: '["uuid", …]' },
          { key: 'pixel_group_ids', label: 'Pixel group ids (JSON list)', type: 'json' },
          { key: 'priority', label: 'Priority' },
        { key: 'is_active', label: 'Active', type: 'check' },
      ]}
      columns={[
        { key: 'name', label: 'Name', render: (r) => <strong>{r.name}</strong> },
        { key: 'event_types', label: 'Events', render: (r) => (r.event_types && r.event_types.length ? r.event_types.join(', ') : 'all') },
        { key: 'filters', label: 'Filters', render: (r) => JSON.stringify(r.filters || {}) },
        { key: 'destination_names', label: 'Destinations', render: (r) => (r.destination_names || []).join(', ') },
        { key: 'pixel_group_names', label: 'Groups', render: (r) => (r.pixel_group_names || []).join(', ') },
        { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
      ]}
    />
  );
}

export function SignalBridgeCollect() {
  const stores = useUniList(`${SB_API}/stores/`);
  const opts = (stores.rows || [])
    .filter((s) => s.platform === 'SHOPIFY')
    .map((s) => ({ value: s.id, label: s.store_domain }));
  const [snip, setSnip] = React.useState(null);
  const [includeMeta, setIncludeMeta] = React.useState(true);
  const [includeGoogle, setIncludeGoogle] = React.useState(true);
  const [copiedId, setCopiedId] = React.useState('');
  const [copyNote, setCopyNote] = React.useState('');
  const snippetUrl = (row) => (
    `${SB_API}/snippet/?collection_key=${row.id}&meta=${includeMeta ? '1' : '0'}&google=${includeGoogle ? '1' : '0'}`
  );
  const partText = (data, key) => (data && typeof data[key] === 'string' ? data[key] : '');
  const loadSnippet = async (row) => {
    setCopyNote('');
    try {
      const data = await getJSON(snippetUrl(row));
      setSnip(data);
    } catch (err) {
      setSnip({ error: String(err.message || err) });
    }
  };
  const copyPart = async (row, key, note) => {
    setCopyNote('');
    try {
      const data = await getJSON(snippetUrl(row));
      setSnip(data);
      const text = partText(data, key) || (key === 'pixel_snippet' ? partText(data, 'snippet') : '');
      if (!text) {
        setCopyNote('No install script was returned for this store.');
        return;
      }
      const ok = await copyToClipboard(text);
      if (!ok) {
        setCopyNote('The browser blocked clipboard access. Select the script below and copy it.');
        return;
      }
      setCopiedId(`${key}:${row.id}`);
      setCopyNote(note);
      window.setTimeout(() => setCopiedId((cur) => (cur === `${key}:${row.id}` ? '' : cur)), 2000);
    } catch (err) {
      setSnip({ error: String(err.message || err) });
    }
  };
  const copySnippet = (row) => copyPart(
    row,
    'pixel_snippet',
    'Copied the custom pixel script. In Shopify, open Settings → Customer events → Add custom pixel, name it SignalBridge, paste the script, save, and click Connect.',
  );
  const copyTheme = (row) => copyPart(
    row,
    'theme_snippet',
    'Copied the theme script. In Shopify, open Online Store → Themes → Edit code → theme.liquid and paste it just before </body>.',
  );
  return (
    <>
      <p className="uni-num--muted" style={{ margin: '0 24px 12px' }}>
        This list is one row per Shopify storefront, not one row per GoKwik connection. A shop you connected as both Shopify and GoKwik is still a single snippet.
        Every store loads the same static file, /static/signalbridge/collect.js. The web server sends that file as-is.
        The snippet only contains this organisation and store's settings: collect URL, snippet version, Meta, and Google labels.
        A store that already has the long pasted script keeps working until you replace it with this install.
        Copy snippet for the custom pixel, then Copy theme for theme.liquid.
        PageView and ViewContent then send the page URL, browser id, click id, and the visitor IP that Tracking health checks.
        Turning Meta or Google off changes the settings in the snippet, so copy the install again after changing those checkboxes.
      </p>
      <div style={{ margin: '0 24px 12px', display: 'flex', gap: 16 }}>
        <label><input type="checkbox" checked={includeMeta} onChange={(e) => { setIncludeMeta(e.target.checked); setSnip(null); }} /> Meta collect</label>
        <label><input type="checkbox" checked={includeGoogle} onChange={(e) => { setIncludeGoogle(e.target.checked); setSnip(null); }} /> Google gtag</label>
      </div>
      <CrudPage
        {...P} helpPage="collect" icon="◎" title="Browser collect"
        endpoint={`${SB_API}/collection-keys/`}
        createLabel="+ Collection key"
        fields={[
          { key: 'name', label: 'Name', required: true },
          { key: 'store_connection', label: 'Store', type: 'select', required: true, options: opts },
          { key: 'allowed_origins', label: 'Extra allowed origins (JSON list)' },
          { key: 'rate_limit_per_minute', label: 'Rate limit / minute' },
          { key: 'is_active', label: 'Active', type: 'check' },
        ]}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'store_domain', label: 'Shopify store' },
          { key: 'key', label: 'Key' },
          { key: 'collect_url', label: 'Collect URL', render: (r) => <code style={{ fontSize: 11 }}>{r.collect_url}</code> },
          { key: 'rate_limit_per_minute', label: 'RPM', align: 'right' },
        ]}
        extraRowActions={(r) => ([
          { key: 'copy', label: copiedId === `pixel_snippet:${r.id}` ? 'Copied' : 'Copy snippet', onClick: () => copySnippet(r) },
          { key: 'theme', label: copiedId === `theme_snippet:${r.id}` ? 'Copied' : 'Copy theme', onClick: () => copyTheme(r) },
          { key: 'snip', label: 'Show install', onClick: () => loadSnippet(r) },
        ])}
      />
      {copyNote ? <p className="uni-num--muted" style={{ margin: '0 24px 12px' }} role="status">{copyNote}</p> : null}
      {snip && (
        <div style={{ margin: '0 24px 24px' }}>
          <p>
            Custom pixel: Settings → Customer events → Add custom pixel, name it SignalBridge, paste the pixel script, then Connect.
            Theme: paste the theme script in theme.liquid just before {'</body>'}.
          </p>
          <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="ty-btn ty-btn--primary"
              onClick={async () => {
                const text = partText(snip, 'pixel_snippet') || partText(snip, 'snippet');
                if (!text) {
                  setCopyNote('No custom pixel script was returned for this store.');
                  return;
                }
                const ok = await copyToClipboard(text);
                setCopyNote(ok
                  ? 'Copied the custom pixel script. In Shopify, open Settings → Customer events → Add custom pixel, name it SignalBridge, paste the script, save, and click Connect.'
                  : 'The browser blocked clipboard access. Select the custom pixel script below and copy it.');
              }}
            >
              Copy snippet
            </button>
            <button
              type="button"
              className="ty-btn ty-btn--secondary"
              onClick={async () => {
                const text = partText(snip, 'theme_snippet');
                if (!text) {
                  setCopyNote('No theme script was returned for this store.');
                  return;
                }
                const ok = await copyToClipboard(text);
                setCopyNote(ok
                  ? 'Copied the theme script. In Shopify, open Online Store → Themes → Edit code → theme.liquid and paste it just before </body>.'
                  : 'The browser blocked clipboard access. Select the theme script below and copy it.');
              }}
            >
              Copy theme
            </button>
          </p>
          <CopyField label="Static collector (same file for every store)" value={snip.script_url} />
          <CopyField label="Collect URL" value={snip.collect_url} />
          <TechDetails title="Custom pixel script" data={snip.pixel_snippet || snip.snippet} />
          <TechDetails title="Theme script" data={snip.theme_snippet} />
          <TechDetails title="Config" data={snip.config} />
        </div>
      )}
    </>
  );
}

export function SignalBridgeApiKeys() {
  const [created, setCreated] = React.useState(null);
  const list = useUniList(`${SB_API}/api-keys/`);
  const [name, setName] = React.useState('Reporting key');
  const create = async () => {
    try {
      const data = await postJSON(`${SB_API}/api-keys/`, { name, can_ingest: true, can_read_reports: true });
      setCreated(data);
      list.reload && list.reload();
    } catch (err) {
      setCreated({ error: String(err.message || err) });
    }
  };
  return (
    <UniPageShell {...P} icon="🔑" title="API keys" helpPage="api_keys" actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}>
      <div className="uni-toolbar" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Key name" />
        <UniActionButton hero primary onClick={create}>Create key</UniActionButton>
      </div>
      {created && created.key && (
        <div className="uni-note" style={{ marginBottom: 16 }}>
          Copy this key now — it is not shown again:
          <pre style={{ whiteSpace: 'pre-wrap' }}>{created.key}</pre>
        </div>
      )}
      {created && created.error && <div className="ty-error">{created.error}</div>}
      <UniTable
        rows={list.rows} loading={list.loading} error={list.error} empty="No API keys"
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'key_prefix', label: 'Prefix' },
          { key: 'can_ingest', label: 'Ingest', render: (r) => (r.can_ingest ? 'yes' : 'no') },
          { key: 'can_read_reports', label: 'Read', render: (r) => (r.can_read_reports ? 'yes' : 'no') },
          { key: 'last_used_at', label: 'Last used' },
          { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
        ]}
      />
    </UniPageShell>
  );
}

export function SignalBridgeAlerts() {
  const dests = useUniList(`${SB_API}/destinations/`);
  const opts = [{ value: '', label: 'Any destination' }].concat(
    (dests.rows || []).map((d) => ({ value: d.id, label: d.name })),
  );
  const logs = useUniList(`${SB_API}/alert-logs/`);
  return (
    <>
      <CrudPage
        {...P} helpPage="alerts" icon="!" title="Alert rules"
        endpoint={`${SB_API}/alert-rules/`}
        createLabel="+ Rule"
        fields={[
          { key: 'name', label: 'Name', required: true },
          { key: 'rule_type', label: 'Type', type: 'select', required: true, options: [
            { value: 'FAILURE_SPIKE', label: 'Failure spike' },
            { value: 'NO_EVENTS_RECEIVED', label: 'No events received' },
            { value: 'DESTINATION_DOWN', label: 'Destination down' },
            { value: 'TOKEN_EXPIRING', label: 'Meta 401 / 190' },
            { value: 'HEALTH_CRITICAL', label: 'Health check critical' },
          ] },
          { key: 'threshold_n', label: 'N (count)' },
          { key: 'window_minutes', label: 'Window minutes' },
          { key: 'consecutive_x', label: 'Consecutive failures' },
          { key: 'destination', label: 'Destination (optional)', type: 'select', options: opts },
          { key: 'channel_email', label: 'Email', type: 'check' },
          { key: 'channel_sms', label: 'SMS', type: 'check' },
          { key: 'recipients_email', label: 'Email recipients (JSON list)' },
          { key: 'recipients_sms', label: 'SMS recipients (JSON list)' },
          { key: 'cooldown_minutes', label: 'Cooldown minutes' },
          { key: 'is_active', label: 'Active', type: 'check' },
        ]}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'rule_type', label: 'Type', render: (r) => <UniStatusPill value={r.rule_type} /> },
          { key: 'last_fired_at', label: 'Last fired' },
          { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
        ]}
      />
      <UniSection title="Recent alerts" />
      <UniTable
        rows={logs.rows} loading={logs.loading} empty="No alerts fired yet"
        columns={[
          { key: 'fired_at', label: 'When' },
          { key: 'rule_name', label: 'Rule' },
          { key: 'message', label: 'Message' },
        ]}
      />
    </>
  );
}

export function SignalBridgeReports() {
  const [kind, setKind] = React.useState('FUNNEL');
  const [since, setSince] = React.useState('');
  const [until, setUntil] = React.useState('');
  const qs = new URLSearchParams({ type: kind });
  if (since) qs.set('since', since);
  if (until) qs.set('until', until);
  const { raw, loading, error, reload } = useUniList(`${SB_API}/reports/?${qs}`, [kind, since, until]);
  const exportFmt = (fmt) => {
    window.location.href = `${SB_API}/reports/?${qs}&format=${fmt}`;
  };
  return (
    <UniPageShell
      {...P} icon="▤" title="Reports" helpPage="reports"
      actions={(
        <>
          <UniActionButton hero onClick={() => exportFmt('csv')}>CSV</UniActionButton>
          <UniActionButton hero onClick={() => exportFmt('xlsx')}>Excel</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      <div className="uni-toolbar" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          {['FUNNEL', 'PURCHASE', 'BUYER', 'ABANDON_CART', 'MATCH_QUALITY', 'COVERAGE', 'DELIVERY', 'PIXEL_COMPARE', 'UTM', 'REFUNDS', 'MERGE'].map((k) => <option key={k}>{k}</option>)}
        </select>
        <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        <UniActionButton hero onClick={() => reload && reload()}>Refresh</UniActionButton>
      </div>
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      {kind === 'BUYER' && !loading && raw && <BuyerReportBody data={raw} />}
      {kind === 'FUNNEL' && (
        <UniTable
          rows={raw?.steps || []} empty="No funnel data" rowKey="event_type"
          columns={[
            { key: 'event_type', label: 'Step' },
            { key: 'count', label: 'Count', align: 'right' },
            { key: 'conversion_from_previous_pct', label: 'Conv %', align: 'right' },
          ]}
        />
      )}
      {kind === 'PURCHASE' && (
        <>
          <p>Prepaid {raw?.prepaid?.count} / ₹{raw?.prepaid?.revenue} AOV {raw?.prepaid?.aov} · COD {raw?.cod?.count} / ₹{raw?.cod?.revenue} AOV {raw?.cod?.aov}</p>
          <UniTable
            rows={raw?.daily || []} empty="No days" rowKey="date"
            columns={[
              { key: 'date', label: 'Date' },
              { key: 'prepaid', label: 'Prepaid', align: 'right' },
              { key: 'cod', label: 'COD', align: 'right' },
              { key: 'revenue', label: 'Revenue', align: 'right' },
            ]}
          />
        </>
      )}
      {kind === 'ABANDON_CART' && (
        <>
          <p>
            {raw?.count || 0} abandoned carts · {raw?.sent || 0} sent to ads ·
            cart value ₹{raw?.value_sum || '0'}
          </p>
          <UniTable
            rows={raw?.rows || []}
            empty="No GoKwik abandoned carts in this range"
            rowKey="id"
            columns={[
              { key: 'event_id', label: 'Event', render: (r) => <Link to={`/signalbridge/events/${r.id}`}>{r.event_id}</Link> },
              { key: 'event_time', label: 'When' },
              { key: 'value', label: 'Value', align: 'right' },
              { key: 'content_name', label: 'Products' },
              { key: 'num_items', label: 'Items', align: 'right' },
              { key: 'city', label: 'City' },
              { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
              { key: 'abc_url', label: 'Recovery URL', render: (r) => (r.abc_url ? <CopyField compact value={r.abc_url} /> : '—') },
            ]}
          />
        </>
      )}
      {kind === 'MATCH_QUALITY' && (
        <UniTable
          rows={raw?.fields || []} empty="No sent purchases" rowKey="field"
          columns={[
            { key: 'field', label: 'Field' },
            { key: 'present', label: 'Present', align: 'right' },
            { key: 'total', label: 'Total', align: 'right' },
            { key: 'pct', label: '%', align: 'right' },
            {
              key: 'band',
              label: 'Band',
              render: (r) => (
                <span style={{ color: r.band === 'green' ? '#15803d' : r.band === 'amber' ? '#b45309' : '#b91c1c' }}>{r.band}</span>
              ),
            },
          ]}
        />
      )}
      {kind === 'COVERAGE' && (raw?.stores || []).map((s) => (
        <div key={s.store}>
          <UniSection title={`${s.store_domain} ${s.has_token ? '' : '(no Admin token — Shopify counts are 0)'}`} />
          <UniTable
            rows={s.days || []} rowKey="date" empty="No days"
            columns={[
              { key: 'date', label: 'Date' },
              { key: 'shopify_orders', label: 'Shopify orders', align: 'right' },
              { key: 'sent_purchases', label: 'SENT purchases', align: 'right' },
              { key: 'gap', label: 'Gap', align: 'right' },
            ]}
          />
        </div>
      ))}
      {kind === 'DELIVERY' && (
        <UniTable
          rows={raw?.destinations || []} empty="No destinations" rowKey="id"
          columns={[
            { key: 'name', label: 'Destination' },
            { key: 'destination_type', label: 'Type' },
            { key: 'error_streak', label: 'Streak', align: 'right' },
            { key: 'h24', label: '24h success %', render: (r) => r.windows?.h24?.success_rate_pct ?? '—' },
            { key: 'd7', label: '7d success %', render: (r) => r.windows?.d7?.success_rate_pct ?? '—' },
            { key: 'd30', label: '30d success %', render: (r) => r.windows?.d30?.success_rate_pct ?? '—' },
          ]}
        />
      )}
      {kind === 'PIXEL_COMPARE' && (
        <>
          <UniTable
            rows={raw?.destinations || []} empty="No destinations" rowKey="id"
            columns={[
              { key: 'name', label: 'Destination' },
              { key: 'destination_type', label: 'Type' },
              { key: 'events_sent', label: 'Sent', align: 'right' },
              { key: 'success_rate_pct', label: 'Success %', align: 'right' },
              { key: 'rejected', label: 'Rejected', align: 'right' },
              { key: 'avg_emq_fields', label: 'Avg EMQ fields', align: 'right' },
            ]}
          />
          <UniSection title={`Inconsistent orders (${raw?.inconsistent_count || 0})`} />
          <UniTable
            rows={raw?.inconsistent_orders || []} empty="All Meta pixels agree" rowKey="event_id"
            columns={[
              { key: 'event_id', label: 'Event id' },
              { key: 'statuses', label: 'Per dest', render: (r) => JSON.stringify(r.statuses) },
            ]}
          />
        </>
      )}
      {kind === 'UTM' && (
        <UniTable
          rows={raw?.rows || []} empty="No UTM rows" rowKey={(r, i) => `${r.utm_source}-${r.utm_medium}-${r.utm_campaign}-${i}`}
          columns={[
            { key: 'utm_source', label: 'Source' },
            { key: 'utm_medium', label: 'Medium' },
            { key: 'utm_campaign', label: 'Campaign' },
            { key: 'count', label: 'Purchases', align: 'right' },
            { key: 'revenue', label: 'Revenue', align: 'right' },
            { key: 'prepaid', label: 'Prepaid', align: 'right' },
            { key: 'cod', label: 'COD', align: 'right' },
          ]}
        />
      )}
      {kind === 'REFUNDS' && (
        <UniTable
          rows={raw?.rows || []} empty="No refunds" rowKey="id"
          columns={[
            { key: 'event_id', label: 'Event id' },
            { key: 'event_type', label: 'Type' },
            { key: 'order_id', label: 'Order', render: (r) => r.shopify_order_number || r.order_id || '—' },
            { key: 'value', label: 'Value', align: 'right' },
            { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
          ]}
        />
      )}
      {kind === 'MERGE' && (
        <>
          <p>
            Merge rate {raw?.merge_rate_pct ?? '—'}% · timed out {raw?.timed_out ?? 0} · late partner {raw?.late_partner ?? 0} · avg wait {raw?.avg_wait_seconds ?? '—'}s
          </p>
          <UniTable
            rows={raw?.groups || []} empty="No merge groups in this window" rowKey="id"
            columns={[
              { key: 'correlation_key', label: 'Key' },
              { key: 'status', label: 'Status', render: (r) => <UniStatusPill value={r.status} /> },
              { key: 'late_partner', label: 'Late', render: (r) => (r.late_partner ? 'yes' : '') },
              { key: 'enrichment_missed', label: 'Missed fields', render: (r) => (r.enrichment_missed ? 'yes' : '') },
              { key: 'created_at', label: 'Created' },
            ]}
          />
          {raw?.conflicts_by_field && Object.keys(raw.conflicts_by_field).length > 0 && (
            <TechDetails title="Field conflicts" data={raw.conflicts_by_field} />
          )}
        </>
      )}
      <CrudPage
        {...P} helpPage="reports" icon="▤" title="Scheduled reports"
        endpoint={`${SB_API}/scheduled-reports/`}
        createLabel="+ Schedule"
        fields={[
          { key: 'name', label: 'Name', required: true },
          { key: 'report_type', label: 'Type', type: 'select', options: [
            { value: 'FUNNEL', label: 'Funnel' },
            { value: 'PURCHASE', label: 'Purchase' },
            { value: 'MATCH_QUALITY', label: 'Match quality' },
            { value: 'COVERAGE', label: 'Coverage' },
            { value: 'DELIVERY', label: 'Delivery' },
            { value: 'PIXEL_COMPARE', label: 'Pixel comparison' },
            { value: 'UTM', label: 'UTM' },
            { value: 'REFUNDS', label: 'Refunds' },
            { value: 'MERGE', label: 'Merge' },
            { value: 'BUYER', label: 'Purchase audience' },
            { value: 'DAILY_DIGEST', label: 'Daily digest' },
          ] },
          { key: 'frequency', label: 'Frequency', type: 'select', options: [
            { value: 'DAILY', label: 'Daily' },
            { value: 'WEEKLY', label: 'Weekly (Monday)' },
          ] },
          { key: 'send_hour', label: 'Send hour (0–23, Asia/Kolkata, for digest)' },
          { key: 'recipients', label: 'Recipients (JSON email list)' },
          { key: 'is_active', label: 'Active', type: 'check' },
        ]}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'report_type', label: 'Type' },
          { key: 'frequency', label: 'Freq' },
          { key: 'last_sent_at', label: 'Last sent' },
        ]}
      />
    </UniPageShell>
  );
}

export function SignalBridgeAudit() {
  const list = useUniList(`${SB_API}/audit/`);
  return (
    <UniPageShell {...P} icon="📜" title="Audit log" helpPage="audit" actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}>
      <UniTable
        rows={list.rows} loading={list.loading} error={list.error} empty="No audit entries"
        columns={[
          { key: 'timestamp', label: 'When' },
          { key: 'user_name', label: 'Who' },
          { key: 'action', label: 'Action', render: (r) => <UniStatusPill value={r.action} /> },
          { key: 'entity_type', label: 'Entity' },
          { key: 'entity_title', label: 'Title' },
        ]}
      />
    </UniPageShell>
  );
}

function screenReachable(screens, id) {
  for (const s of screens) {
    if (s.id === id) return true;
    if (!s.ok) return false;
  }
  return true;
}

export function SignalBridgeOnboarding() {
  const { raw, loading, error, reload } = useUniList(`${SB_API}/onboarding/`);
  const screens = raw?.screens || [];
  const recommended = raw?.current_screen || (screens[0] && screens[0].id) || 'store';
  const [pinned, setPinned] = React.useState(null);
  const [busyForce, setBusyForce] = React.useState(false);
  const [forceError, setForceError] = React.useState('');
  const wizardForceRef = React.useRef(null);
  const activeId = pinned || (recommended === 'done'
    ? (screens[screens.length - 1] && screens[screens.length - 1].id)
    : recommended);
  const screen = screens.find((s) => s.id === activeId) || screens[0];
  const live = raw?.live || {};

  const onWizardFinished = React.useCallback(() => {
    setPinned(null);
    if (reload) reload();
  }, [reload]);

  const forceNext = async () => {
    if (!screen) return;
    setBusyForce(true);
    setForceError('');
    try {
      if (wizardForceRef.current) {
        try {
          await wizardForceRef.current();
        } catch {
          /* still mark the onboarding screen so a stuck check cannot trap the operator */
        }
      }
      await patchJSON(`${SB_API}/onboarding/`, { complete_screen: screen.id, force: true });
      setPinned(null);
      if (reload) reload();
    } catch (err) {
      setForceError(String(err.message || err));
    } finally {
      setBusyForce(false);
    }
  };

  const checkAgain = () => {
    if (reload) reload();
  };

  return (
    <UniPageShell
      {...P}
      icon="1"
      title="Get started in minutes"
      subtitle="Each screen opens its wizard, checks the connection, then unlocks the next one. Use Force next only if a check is stuck."
      helpPage="onboarding"
      helpApi={SB_API}
      helpOpen={false}
      actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <div className="sb-trust">
        <span>Set up in minutes</span>
        <span>No coding required</span>
        <span>Data in Meta and Google</span>
      </div>
      {!!screens.length && (
        <ol className="sb-onboard-steps">
          {screens.map((s) => {
            const on = s.id === (screen && screen.id);
            const reachable = screenReachable(screens, s.id);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={`sb-onboard-step${on ? ' sb-onboard-step--on' : ''}${s.ok ? ' sb-onboard-step--done' : ''}${s.forced ? ' sb-onboard-step--forced' : ''}`}
                  disabled={!reachable}
                  onClick={() => reachable && setPinned(s.id)}
                >
                  <span className="sb-onboard-step__n">{s.ok ? '✓' : s.n}</span>
                  <span>{s.title}</span>
                  {s.forced ? <span className="sb-onboard-step__forced">Forced</span> : null}
                </button>
              </li>
            );
          })}
        </ol>
      )}
      {raw?.all_done && (
        <div className="sb-live" style={{ marginTop: 8 }}>
          <div>
            <p className="sb-live__title">Tracking setup is complete</p>
            <p className="sb-live__meta">
              Last store event {live.last_received_at || 'none yet'} · last send to ads {live.last_sent_at || 'none yet'}
            </p>
          </div>
          <UniActionButton primary to="/signalbridge/events">Open event log</UniActionButton>
        </div>
      )}
      {screen && (
        <div className={`sb-onboard-screen${screen.ok ? ' sb-onboard-screen--ok' : ''}`}>
          <h2>{screen.title}</h2>
          <p>{screen.body}</p>
          {screen.forced && (
            <p className="sb-force-note">This screen was forced forward (not recommended). Live checks have not passed yet.</p>
          )}
          <OnboardChecks checks={screen.checks} />
          <div className="sb-onboard-actions">
            <UniActionButton hero onClick={checkAgain}>Check again</UniActionButton>
            {screen.live_ok && !raw?.all_done && recommended !== screen.id && (
              <UniActionButton hero primary onClick={() => setPinned(null)}>Continue to next screen</UniActionButton>
            )}
            {screen.live_ok && recommended === screen.id && raw?.all_done && (
              <UniActionButton hero primary to={HOME}>Go to SignalBridge home</UniActionButton>
            )}
            {!screen.ok && (
              <ForceNextButton last={screen.n >= screens.length} disabled={busyForce} onClick={forceNext} />
            )}
          </div>
          {forceError && <div className="ty-error">{forceError}</div>}
          {screen.wizard === 'store' && (
            <SignalBridgeStoreWizard embedded onFinished={onWizardFinished} forceRef={wizardForceRef} />
          )}
          {screen.wizard === 'pixel' && (
            <>
              <SignalBridgePixelWizard embedded onFinished={onWizardFinished} forceRef={wizardForceRef} />
              <details className="sb-onboard-optional">
                <summary>Optional: Google Ads</summary>
                <SignalBridgeGoogleWizard embedded onFinished={onWizardFinished} />
              </details>
              <p className="uni-num--muted">
                Fine-tune which events to send after the pixel is saved.
                {' '}
                <UniActionButton to={live.pixel_id ? `/signalbridge/configure-events?pixel=${live.pixel_id}` : '/signalbridge/configure-events'}>Configure events</UniActionButton>
              </p>
            </>
          )}
          {screen.wizard === 'test' && (
            <>
              <h3>Send a test</h3>
              <p className="uni-num--muted">Paste a Test Events code. This does not edit the pixel. After a successful send, click Check again.</p>
              <TestEventForm onSent={onWizardFinished} />
              <details className="sb-onboard-optional">
                <summary>Optional: choose which events and fields to send</summary>
                <SignalBridgeEventsWizard embedded onFinished={onWizardFinished} />
              </details>
            </>
          )}
        </div>
      )}
      <p className="uni-num--muted" style={{ marginTop: 16 }}>
        Last store event: {live.last_received_at || 'none yet'} · Last send to ads: {live.last_sent_at || 'none yet'}
      </p>
    </UniPageShell>
  );
}

export function SignalBridgeSettings() {
  const { raw, loading, error, reload } = useUniList(`${SB_API}/settings/`);
  const tz = useDisplayTimezone();
  const [form, setForm] = React.useState({});
  React.useEffect(() => {
    if (raw) {
      setForm({
        retention_days: raw.retention_days ?? 180,
        collect_rate_per_minute: raw.collect_rate_per_minute ?? 100,
        retry_max_attempts: raw.retry_max_attempts ?? 5,
        retry_backoff_base_seconds: raw.retry_backoff_base_seconds ?? 2,
        capi_batch_size: raw.capi_batch_size ?? 500,
        google_batch_size: raw.google_batch_size ?? 2000,
        meta_graph_base: raw.meta_graph_base || '',
        webhook_callback_base: raw.webhook_callback_base || '',
        inbound_liveness_multiplier: raw.inbound_liveness_multiplier ?? 3,
        emq_drift_drop_pct: raw.emq_drift_drop_pct ?? 20,
        merge_health_warning_pct: raw.merge_health_warning_pct ?? 15,
        click_id_coverage_warning_pct: raw.click_id_coverage_warning_pct ?? 20,
      });
    }
  }, [raw]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    const payload = { ...form };
    ['retention_days', 'collect_rate_per_minute', 'retry_max_attempts', 'retry_backoff_base_seconds',
      'capi_batch_size', 'google_batch_size', 'inbound_liveness_multiplier', 'emq_drift_drop_pct',
      'merge_health_warning_pct', 'click_id_coverage_warning_pct'].forEach((k) => {
      payload[k] = Number(payload[k]);
    });
    await patchJSON(`${SB_API}/settings/`, payload);
    reload && reload();
  };
  const Field = ({ k, label, type = 'number', hint }) => (
    <label style={{ display: 'grid', gap: 4 }}>
      {label}
      <input type={type} value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
      {hint && <span className="uni-num--muted" style={{ fontSize: 12 }}>{hint}</span>}
    </label>
  );
  return (
    <UniPageShell {...P} icon="⚙" title="SignalBridge settings" helpPage="settings" helpApi={SB_API} actions={<UniActionButton hero to={HOME}>← Home</UniActionButton>}>
      <UniSection title="Organisation timezone" />
      <p className="uni-num--muted">
        Shopify order times on the event log use the timezone organisation
        admins set in Business → Timezone. It cannot be overridden here.
      </p>
      <div style={{ display: 'grid', gap: 12, maxWidth: 560, marginBottom: 24 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          Timezone
          <input type="text" value={tz} readOnly />
        </label>
        <p className="uni-num--muted" style={{ fontSize: 12 }}>
          Preview: {formatInTimeZone(new Date().toISOString(), tz)}
        </p>
        <div>
          <UniActionButton hero to="/business/timezone">Open Business → Timezone</UniActionButton>
        </div>
      </div>
      <UniSection title="Organisation knobs" />
      {error ? <div className="ty-error">{String(error.message || error)}</div> : null}
      <p className="uni-num--muted">
        These knobs live in this organisation&apos;s database, not in .env.
        Only Fernet, INLINE, and TRUST_PROXY stay in the environment.
      </p>
      <div style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
        <Field k="retention_days" label="Retention days (purge raw payloads, keep counters)" />
        <Field k="collect_rate_per_minute" label="Browser collect rate limit (req/min)" />
        <Field k="retry_max_attempts" label="Delivery retry max attempts" />
        <Field k="retry_backoff_base_seconds" label="Retry backoff base seconds" />
        <Field k="capi_batch_size" label="Meta CAPI batch size" />
        <Field k="google_batch_size" label="Google Ads upload batch size" />
        <Field k="meta_graph_base" label="Meta Graph host" type="text" hint="Blank = https://graph.facebook.com" />
        <Field k="webhook_callback_base" label="Public webhook callback origin" type="text" hint="Unused for the Partners Shopify app (callbacks go to https://tasky.do/api/…). Optional override for custom setups." />
        <Field k="inbound_liveness_multiplier" label="Inbound liveness gap multiplier" />
        <Field k="emq_drift_drop_pct" label="EMQ drift warning drop %" />
        <Field k="merge_health_warning_pct" label="Merge unmerged/timeout warning %" />
        <Field k="click_id_coverage_warning_pct" label="Google click-id coverage warning %" />
      </div>
      <div style={{ marginTop: 12 }}>
        <UniActionButton hero primary onClick={save}>Save</UniActionButton>
      </div>
    </UniPageShell>
  );
}

const MATCH_STATUS = { HEALTHY: 'Working', WARNING: 'Partial', CRITICAL: 'Not working', UNKNOWN: 'Not enough data' };
const MATCH_DOT = { HEALTHY: '#16a34a', WARNING: '#d97706', CRITICAL: '#dc2626', UNKNOWN: '#94a3b8' };

function matchTableId(eventType) {
  return `match-fields-${eventType}`;
}

function MatchQualityMonitor({ reloadToken = 0 }) {
  const tz = useDisplayTimezone();
  const mon = useUniList(`${SB_API}/match-quality/`, [reloadToken]);
  const data = mon.raw || {};
  const [openTable, setOpenTable] = React.useState('');
  const sinceText = data.scored_since
    ? formatInTimeZone(data.scored_since, tz)
    : `the last ${data.window_days || 7} days`;
  const eventTypes = new Set((data.events || []).map((event) => event.event_type));
  if (mon.loading && !data.checks) return <div className="uni-state">Loading match quality…</div>;
  if (mon.error) return <div className="ty-error">{String(mon.error.message || mon.error)}</div>;
  if (!data.checks) return null;
  return (
    <>
      <UniSection title="Match quality" />
      <p className="uni-num--muted" style={{ marginTop: 0 }}>{data.help}</p>
      <p style={{ marginTop: 0 }}><strong>{data.summary}</strong> Events that reached Meta since {sinceText}.</p>
      {data.fresh_since ? (
        <p className="uni-num--muted" style={{ marginTop: 0 }}>
          This window starts at the health refresh on {formatInTimeZone(data.fresh_since, tz)}.
          Events received before that refresh remain stored.
        </p>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
        {(data.checks || []).map((check) => (
          <div key={check.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 99, background: MATCH_DOT[check.status] || MATCH_DOT.UNKNOWN }} />
              <strong>{check.title}</strong>
            </div>
            <div style={{ marginTop: 6, fontSize: 13 }}>{MATCH_STATUS[check.status] || check.status}</div>
            <div className="uni-num--muted">{check.detail}</div>
            {(check.how_to || []).length ? (
              <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
                {check.how_to.map((line) => <li key={line} style={{ marginBottom: 4 }}>{line}</li>)}
              </ol>
            ) : null}
            {(eventTypes.has(check.id) || check.href) ? (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {eventTypes.has(check.id) ? (
                  <a
                    className="ty-btn ty-btn--secondary"
                    href={`#${matchTableId(check.id)}`}
                    style={{ textDecoration: 'none' }}
                    onClick={() => setOpenTable(check.id)}
                  >
                    View the table
                  </a>
                ) : null}
                {check.href ? (
                  <UniActionButton to={check.href}>{check.href_label || 'Configure'}</UniActionButton>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {(data.events || []).map((event) => (
        <div
          key={event.event_type}
          id={matchTableId(event.event_type)}
          style={{
            scrollMarginTop: 12,
            borderRadius: 12,
            outline: openTable === event.event_type ? '2px solid #2563eb' : 'none',
            outlineOffset: 8,
          }}
        >
          <UniSection title={`${event.event_type} · ${event.sent} sent · ${MATCH_STATUS[event.status] || event.status}`} />
          <UniTable
            rows={event.fields || []}
            rowKey="field"
            empty="No events"
            columns={[
              {
                key: 'label',
                label: 'Parameter',
                render: (row) => (
                  <div>
                    <div>{row.label}</div>
                    {(row.how_to || []).length ? (
                      <ol style={{ margin: '6px 0 0', paddingLeft: 18 }} className="uni-num--muted">
                        {row.how_to.map((line) => <li key={line} style={{ marginBottom: 4 }}>{line}</li>)}
                      </ol>
                    ) : null}
                    {row.href ? (
                      <div style={{ marginTop: 8 }}>
                        <UniActionButton to={row.href}>{row.href_label || 'Configure'}</UniActionButton>
                      </div>
                    ) : null}
                  </div>
                ),
              },
              { key: 'present', label: 'On the payload', align: 'right' },
              { key: 'total', label: 'Sent events', align: 'right' },
              { key: 'pct', label: '%', align: 'right' },
              { key: 'status', label: 'Status', render: (row) => MATCH_STATUS[row.status] || row.status },
            ]}
          />
        </div>
      ))}
      <UniSection title="Steps to raise match quality" />
      <ol style={{ margin: '0 0 24px', paddingLeft: 20 }}>
        {(data.steps || []).map((step) => (
          <li key={step.id} style={{ marginBottom: 12 }}>
            <div>
              {step.title}
              {step.needed ? ' — do this next' : ''}
            </div>
            <div className="uni-num--muted">{step.body}</div>
            {step.href ? (
              <div style={{ marginTop: 6 }}>
                <UniActionButton to={step.href}>{step.href_label || 'Open'}</UniActionButton>
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </>
  );
}

export function SignalBridgeConnHealth() {
  const tz = useDisplayTimezone();
  const health = useUniList(`${SB_API}/health/`);
  const conn = useUniList(`${SB_API}/connection-health/`);
  const engine = useUniList(`${SB_API}/health-engine/`);
  const [busy, setBusy] = React.useState('');
  const [problem, setProblem] = React.useState('');
  const [matchTick, setMatchTick] = React.useState(0);
  const DOT = { HEALTHY: '#16a34a', WARNING: '#d97706', CRITICAL: '#dc2626', UNKNOWN: '#94a3b8' };
  const reloadAll = () => {
    engine.reload && engine.reload();
    health.reload && health.reload();
    conn.reload && conn.reload();
    setMatchTick((n) => n + 1);
  };
  const run = async () => {
    setBusy('run');
    setProblem('');
    try {
      await postJSON(`${SB_API}/health-engine/`, { action: 'run' });
      reloadAll();
    } catch (err) {
      setProblem(String(err.message || err));
    } finally {
      setBusy('');
    }
  };
  const refresh = async () => {
    const ok = window.confirm(
      'Refresh health scoring from this moment?\n\n'
      + 'New health parameters will be measured on events received from now on. '
      + 'Earlier events and past health results remain stored.',
    );
    if (!ok) return;
    setBusy('refresh');
    setProblem('');
    try {
      await postJSON(`${SB_API}/health-engine/`, { action: 'refresh' });
      reloadAll();
    } catch (err) {
      setProblem(String(err.message || err));
    } finally {
      setBusy('');
    }
  };
  const freshSince = engine.raw?.fresh_since;
  return (
    <UniPageShell
      {...P} icon="💚" title="Tracking health" helpPage="health" helpApi={SB_API} helpOpen={false}
      subtitle="Match quality shows which customer fields reached Meta, and the steps to raise the ones that did not. Green on a destination means events are arriving."
      actions={(
        <>
          <UniActionButton hero primary disabled={!!busy} onClick={run}>{busy === 'run' ? 'Running…' : 'Run checks now'}</UniActionButton>
          <UniActionButton hero disabled={!!busy} onClick={refresh}>{busy === 'refresh' ? 'Refreshing…' : 'Refresh health data'}</UniActionButton>
          <UniActionButton hero to={HOME}>← Home</UniActionButton>
        </>
      )}
    >
      <p className="uni-num--muted">Snippet version {engine.raw?.current_snippet_version}</p>
      {freshSince ? (
        <p className="uni-num--muted">
          Health scores count events received since {formatInTimeZone(freshSince, tz)}.
          Earlier events and previous health results remain stored.
          Refresh health data again after you add a parameter so the next scores start from that moment.
        </p>
      ) : (
        <p className="uni-num--muted">
          Scores use the recent event window. Refresh health data after you add a health parameter
          so scores count only events received from that moment. Stored events stay in place.
        </p>
      )}
      {problem ? <div className="ty-error">{problem}</div> : null}
      <MatchQualityMonitor reloadToken={matchTick} />
      <UniSection title="Destinations" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
        {(engine.raw?.destinations || []).map((d) => (
          <div key={d.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 99, background: DOT[d.status] || DOT.UNKNOWN }} />
              <strong>{d.name}</strong>
            </div>
            <div className="uni-num--muted">{d.destination_type}</div>
            {(d.checks || []).map((c) => (
              <div key={c.id} style={{ marginTop: 8, fontSize: 13 }}>
                <UniStatusPill value={c.status} /> {c.check_type}
                <div className="uni-num--muted">{c.how_to_fix}</div>
                {c.href ? (
                  <div style={{ marginTop: 6 }}>
                    <UniActionButton to={c.href}>{c.href_label || 'Open'}</UniActionButton>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
      <UniSection title="Stores (inbound liveness + webhook registration)" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12, marginBottom: 16 }}>
        {(engine.raw?.stores || []).map((d) => (
          <div key={d.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: 99, background: DOT[d.status] || DOT.UNKNOWN }} />
              <strong>{d.name}</strong>
            </div>
            <div className="uni-num--muted">{d.platform}</div>
            {(d.checks || []).map((c) => (
              <div key={c.id || c.check_type} style={{ marginTop: 8, fontSize: 13 }}>
                <UniStatusPill value={c.status} /> {c.check_type}
                <div className="uni-num--muted">{c.how_to_fix}</div>
                {c.href ? (
                  <div style={{ marginTop: 6 }}>
                    <UniActionButton to={c.href}>{c.href_label || 'Open'}</UniActionButton>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
      <UniSection title="Merge" />
      {(engine.raw?.merge || []).length === 0 && <p className="uni-num--muted">No merge health rows yet. Pairing is opt-in.</p>}
      {(engine.raw?.merge || []).map((c) => (
        <div key={c.id || c.check_type} style={{ marginBottom: 8, fontSize: 13 }}>
          <UniStatusPill value={c.status} /> {c.check_type}
          <div className="uni-num--muted">{c.how_to_fix}</div>
          {c.href ? (
            <div style={{ marginTop: 6 }}>
              <UniActionButton to={c.href}>{c.href_label || 'Open'}</UniActionButton>
            </div>
          ) : null}
        </div>
      ))}
      <UniSection title="Live check" />
      <TechDetails title="Health probe JSON" data={health.raw} />
      <UniSection title="Stores" />
      <UniTable
        rows={conn.raw?.stores || []} empty="No stores" rowKey="id"
        columns={[
          { key: 'store_domain', label: 'Store' },
          { key: 'platform', label: 'Platform' },
          { key: 'last_event_received_at', label: 'Last inbound' },
          { key: 'last_successful_send_at', label: 'Last sent' },
          { key: 'webhook_secret_set', label: 'HMAC', render: (r) => (r.webhook_secret_set ? 'yes' : 'no') },
        ]}
      />
      <UniSection title="Destinations" />
      <UniTable
        rows={conn.raw?.destinations || []} empty="No destinations" rowKey="id"
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'destination_type', label: 'Type' },
          { key: 'last_success_at', label: 'Last success' },
          { key: 'last_failure_at', label: 'Last failure' },
          { key: 'error_streak', label: 'Streak', align: 'right' },
        ]}
      />
    </UniPageShell>
  );
}
